import { useState, useEffect, useRef } from 'react'
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightStartOnRectangleIcon,
  UserIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  HomeIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid'
import { Link, useLocation } from 'react-router-dom'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { Trophy } from 'lucide-react'

function planBadgeFor(profile) {
  if (profile?.isAdmin) return { label: 'Admin', tone: 'bg-[var(--gold)]/20 text-[var(--gold)]' }
  if (profile?.isPremium)
    return { label: 'Premium', tone: 'bg-[var(--gold)]/15 text-[var(--gold)]' }
  return { label: 'Free', tone: 'bg-[var(--text-muted)]/15 text-[var(--text-muted)]' }
}

// ── Public nav links ─────────────────────────────────────────
// Landing page uses anchor hrefs; other pages use path+hash
// Three placeholder labels for the hero-redesign pass — rename once
// final IA is locked. Routes point at existing pages so links work today.
const LANDING_NAV_LINKS = [
  { label: 'Mission', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
]

const PUBLIC_NAV_LINKS = [
  { label: 'Mission', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
]

function ProfileMenu({ user, profile, firstName, signOut }) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)
  const badge = planBadgeFor(profile)

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return
    const onDocClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => setOpen(false)

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Open profile menu"
        className="flex items-center gap-2 text-[var(--gold)] text-lg font-semibold hover:scale-110 transition-transform duration-200 py-2"
      >
        <UserIcon className="w-5 h-5" aria-hidden="true" />
        Hi, {firstName}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      <div
        role="menu"
        className={`absolute top-full right-0 pt-2 transition-all duration-200 z-50 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
      >
        <div className="rounded-xl shadow-2xl w-64 bg-[var(--bg-surface)] border border-[var(--glass-border)] overflow-hidden">
          {/* Header: email + plan badge */}
          <div className="px-4 py-3 border-b border-[var(--glass-border)]/60">
            <p className="text-xs text-[var(--text-muted)] truncate" title={user?.email}>
              {user?.email}
            </p>
            <span
              className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${badge.tone}`}
            >
              {badge.label}
            </span>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              to="/home"
              onClick={close}
              role="menuitem"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--gold)]/10 hover:text-[var(--gold)] transition-colors"
            >
              <HomeIcon className="w-4 h-4 text-[var(--text-muted)]" aria-hidden="true" />
              Dashboard
            </Link>
            <Link
              to="/learn/achievements"
              onClick={close}
              role="menuitem"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--gold)]/10 hover:text-[var(--gold)] transition-colors"
            >
              <Trophy className="w-4 h-4 text-[var(--text-muted)]" aria-hidden="true" />
              Achievements
            </Link>
            <Link
              to="/account"
              onClick={close}
              role="menuitem"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--gold)]/10 hover:text-[var(--gold)] transition-colors"
            >
              <Cog6ToothIcon className="w-4 h-4 text-[var(--text-muted)]" aria-hidden="true" />
              Account &amp; Preferences
            </Link>
            {profile?.isAdmin && (
              <Link
                to="/admin"
                onClick={close}
                role="menuitem"
                className="flex items-center gap-2.5 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--gold)]/10 hover:text-[var(--gold)] transition-colors"
              >
                <ShieldCheckIcon className="w-4 h-4 text-[var(--text-muted)]" aria-hidden="true" />
                Admin Dashboard
              </Link>
            )}
          </div>

          {/* Sign out (separated) */}
          <div className="py-1 border-t border-[var(--glass-border)]/60">
            <button
              onClick={() => {
                close()
                signOut()
              }}
              role="menuitem"
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-400 transition-colors text-left"
            >
              <ArrowRightStartOnRectangleIcon className="w-4 h-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function NavLink({ link, onClick, className }) {
  const base =
    className ||
    'text-[var(--text-primary)] hover:text-[var(--gold)] hover:scale-110 transition-all duration-200 text-lg font-medium tracking-wide inline-block'

  if (link.href.startsWith('#') || link.href.startsWith('http')) {
    return (
      <a href={link.href} onClick={onClick} className={base}>
        {link.label}
      </a>
    )
  }
  return (
    <Link to={link.href} onClick={onClick} className={base}>
      {link.label}
    </Link>
  )
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  // Landing-only: tracks whether the user has scrolled past the hero so we
  // can slide the navbar off-screen. Threshold = 80% of viewport height so
  // it triggers as the user is leaving the hero, not exactly at the edge.
  const [pastHero, setPastHero] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const isHome = location.pathname === '/'
  const { user, signOut } = useSession()
  const { profile } = useProfile()

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50)
      setPastHero(window.scrollY > window.innerHeight * 0.8)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Lock page scroll while the mobile menu is open. Mason's rule: you have
  // to close the menu before you can scroll the page again. iOS Safari
  // ignores `overflow: hidden` on body for touch-scroll, so the reliable
  // cross-browser pattern is: snapshot current scrollY, freeze body via
  // `position: fixed; top: -scrollY`, then on unlock restore scroll back
  // to where it was. Also block touchmove on the backdrop as a fallback.
  useEffect(() => {
    if (!menuOpen) return
    const scrollY = window.scrollY
    const prevPosition = document.body.style.position
    const prevTop = document.body.style.top
    const prevWidth = document.body.style.width
    const prevOverflow = document.body.style.overflow
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.position = prevPosition
      document.body.style.top = prevTop
      document.body.style.width = prevWidth
      document.body.style.overflow = prevOverflow
      window.scrollTo(0, scrollY)
    }
  }, [menuOpen])

  const firstName = profile?.displayName
    ? profile.displayName.split(' ')[0]
    : user?.email?.split('@')[0] || ''

  // At soft launch (2026-04-28) any logged-in user has app access — free
  // tier included. Per-feature gating (premium chat tabs, message caps,
  // etc.) is enforced inside each feature, not at the navbar level.
  const hasAppAccess = !!user

  // Logged-in users see "Chat" instead of "Features" everywhere they hit the
  // public navbar — they don't need the pitch, they need the entry point.
  // Applies to both the landing page (anchor #features link) and any other
  // public page (path /#features).
  const baseLinks = isHome ? LANDING_NAV_LINKS : PUBLIC_NAV_LINKS
  const midLinks = hasAppAccess
    ? [{ label: 'Chat', href: '/chat' }, ...baseLinks.filter(l => l.label !== 'Features')]
    : baseLinks

  // On landing: navbar is fully transparent at the top (no "top banner"
  // strip over the hero) and slides off-screen once the user scrolls past
  // the hero. On every other page: keep the original behavior — solid bg
  // when scrolled, soft translucent strip at the top.
  const navBg = isHome
    ? 'bg-transparent border-b border-transparent'
    : scrolled
      ? 'bg-[var(--bg-base)]/95 backdrop-blur-md shadow-lg border-b border-[var(--glass-border)]'
      : 'bg-[var(--bg-base)]/70 backdrop-blur-sm border-b border-transparent'
  const navTransform = isHome && pastHero ? '-translate-y-full' : 'translate-y-0'

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 pt-[env(safe-area-inset-top)] ${navTransform} ${navBg}`}
      >
        {/* Edge-to-edge container — no max-width. Responsive horizontal
          padding scales from px-4 (mobile) up to px-24 (2xl) so the logo
          hugs closer to the viewport's left edge on wider monitors and
          the right cluster hugs closer to the right edge. Hero container
          uses identical padding so the logo aligns with the "T" in
          "Tools" across every breakpoint. Navbar h-20 gives the bigger
          logo breathing room. */}
        <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16 2xl:px-24 flex items-center justify-between h-20">
          {/* Logo + wordmark — both larger this pass. */}
          <Link
            to="/"
            aria-label="Illuminate Faith — home"
            className="flex items-center gap-2.5 text-[var(--gold)] font-heading text-2xl font-bold tracking-wide hover:scale-105 transition-transform duration-200 shrink-0 origin-left"
          >
            <img
              src="/FinalLogo.png"
              alt="Illuminate Faith logo"
              className="h-11 md:h-14 w-auto rounded"
              style={{ clipPath: 'inset(2% round 4px)' }}
            />
            <span className="leading-none hidden sm:inline">Illuminate Faith</span>
          </Link>

          {/* Desktop — single right-aligned cluster: nav links, then auth,
            then theme toggle. */}
          <div className="hidden md:flex items-center gap-10">
            <div className="flex items-center gap-9">
              {midLinks.map(link => (
                <NavLink key={`${link.href}-${link.label}`} link={link} />
              ))}
            </div>
            {user ? (
              <ProfileMenu user={user} profile={profile} firstName={firstName} signOut={signOut} />
            ) : (
              <div className="flex items-center gap-5">
                <Link
                  to="/login"
                  className="text-[var(--text-primary)] hover:text-[var(--gold)] hover:scale-110 transition-all duration-200 text-lg font-medium inline-block"
                >
                  Sign In
                </Link>
                <Link
                  to="/login?mode=signup"
                  className="px-8 py-3 rounded-md border-2 border-[var(--gold)] text-[var(--gold)] text-lg font-semibold tracking-wide uppercase hover:bg-[var(--gold)] hover:text-[var(--text-on-gold)] hover:scale-105 hover:shadow-[0_0_30px_rgba(212,168,67,0.5)] transition-all duration-200 inline-block"
                >
                  Try Free
                </Link>
              </div>
            )}
          </div>

          {/* Mobile — hamburger only (theme toggle removed). */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-[var(--text-primary)] w-11 h-11 flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <XMarkIcon className="w-7 h-7" aria-hidden="true" />
              ) : (
                <Bars3Icon className="w-7 h-7" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu — rounded "card" modal anchored under the hamburger
          on the right, with a subtle dim backdrop so the hero stays
          visible on the left. Rendered as a SIBLING of <nav> (not a
          child) because the nav uses `translate-y-0` which establishes
          a containing block for fixed descendants — when the menu was
          inside <nav>, `bottom: 0` resolved to the nav's 81px bottom
          edge and collapsed the menu to 1px tall (verified via
          Playwright). Outside the nav, the viewport is the containing
          block again. */}
      {menuOpen && (
        <>
          {/* Backdrop — subtle dim, taps to close. Kept light so the
              hero scene still reads behind the menu. touchmove preventDefault
              backs up the body-scroll-lock useEffect on the off chance an
              iOS Safari edge case still lets the page slide. */}
          <div
            className="md:hidden fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[54] animate-fade-in"
            onClick={() => setMenuOpen(false)}
            onTouchMove={e => e.preventDefault()}
            aria-hidden="true"
          />
          {/* Modal card — centered horizontally, max-w-[320px], rounded.
              Hero shows on both sides + the bottom. */}
          <div
            className="md:hidden fixed left-1/2 -translate-x-1/2 z-[55] w-[calc(100vw_-_2rem)] max-w-[320px] rounded-2xl bg-[var(--bg-base)]/97 backdrop-blur-md border border-[var(--glass-border)] shadow-2xl animate-fade-in overflow-y-auto overscroll-contain"
            style={{
              top: 'calc(env(safe-area-inset-top) + 4.75rem)',
              maxHeight: 'calc(100dvh - env(safe-area-inset-top) - 6rem)',
            }}
          >
            <div className="px-4 py-4 pb-5 flex flex-col gap-1">
              {user && (
                <div className="py-3 border-b border-[var(--outline-ghost)] mb-2">
                  <div className="flex items-center gap-2 text-[var(--gold)] text-sm font-semibold">
                    <UserIcon className="w-4 h-4" aria-hidden="true" />
                    Hi, {firstName}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <p
                      className="text-[11px] text-[var(--text-muted)] truncate flex-1"
                      title={user.email}
                    >
                      {user.email}
                    </p>
                    {(() => {
                      const badge = planBadgeFor(profile)
                      return (
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${badge.tone}`}
                        >
                          {badge.label}
                        </span>
                      )
                    })()}
                  </div>
                </div>
              )}

              {midLinks.map(link => (
                <NavLink
                  key={`${link.href}-${link.label}`}
                  link={link}
                  onClick={() => setMenuOpen(false)}
                  className="text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors text-sm font-medium py-3 border-b border-[var(--outline-ghost)]/50 block"
                />
              ))}

              {/* Mobile admin — direct link to /admin (per Mason's request,
                no in-menu expandable mega list). The full admin sidebar
                lives on the /admin page itself. */}
              {user && profile?.isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors text-sm font-medium py-3 border-b border-[var(--outline-ghost)]/50"
                >
                  <ShieldCheckIcon className="w-4 h-4" aria-hidden="true" />
                  Admin
                </Link>
              )}

              {!user && (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors text-sm font-medium py-3 border-b border-[var(--outline-ghost)]/50 block"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/login?mode=signup"
                    onClick={() => setMenuOpen(false)}
                    className="mt-3 flex items-center justify-center px-4 py-3 rounded-full bg-[var(--gold)] text-[var(--text-on-gold)] text-sm font-semibold hover:brightness-110 hover:scale-105 hover:shadow-[0_0_25px_rgba(212,168,67,0.5)] transition-all duration-200"
                  >
                    Try Free
                  </Link>
                </>
              )}

              {user && (
                <>
                  <Link
                    to="/learn/achievements"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors text-sm font-medium py-3 border-b border-[var(--outline-ghost)]/50"
                  >
                    <Trophy className="w-4 h-4" aria-hidden="true" />
                    Achievements
                  </Link>
                  <Link
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors text-sm font-medium py-3 border-b border-[var(--outline-ghost)]/50"
                  >
                    <Cog6ToothIcon className="w-4 h-4" aria-hidden="true" />
                    Account &amp; Preferences
                  </Link>
                  <button
                    onClick={() => {
                      signOut()
                      setMenuOpen(false)
                    }}
                    className="flex items-center gap-2 text-[var(--text-muted)] hover:text-red-400 transition-colors text-sm font-medium py-3 mt-1"
                  >
                    <ArrowRightStartOnRectangleIcon className="w-4 h-4" aria-hidden="true" />
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}
